import { Camera, Loader2, X } from 'lucide-react';
import { type ChangeEvent, useMemo, useRef, useState } from 'react';
import {
  PanVerificationLite,
  PanVerificationOCR,
  PanVerificationStandard,
} from '@/features/workflows/components/simple/preview-screens';
import { toast } from '@/lib/notify';

import { useVerifyStep } from '../../api/useVerifyStep';
import type {
  SessionDetailDto,
  SessionStepApiBindingDto,
  SessionStepDto,
} from '../../schemas/sessionSchemas';
import { isSessionExpiredError, SessionExpiredScreen } from '../SessionExpiredScreen';
import {
  CHECK_FAILED_TOAST,
  RETRY_EXHAUSTED_NOTE,
  tryAnotherMethodNote,
} from '../stepFallbackNotes';
import { PanCardMock } from './PanCardMock';
import { PanDevSimulator } from './PanDevSimulator';
import { extractReason, PAN_METHOD_LABEL, type PanScreenKind, panScreenKind } from './pan.utils';

// Indian PAN format: 5 letters + 4 digits + 1 letter (e.g. ABCDE1234F). The
// input is upper-cased as the applicant types, so we test the upper-cased value.
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

// Fallback binding for legacy sessions whose snapshot carries no bindings —
// submits without a catalogueEntryId, letting the backend run its own chain.
const DEFAULT_BINDING: SessionStepApiBindingDto = {
  catalogueEntryId: '',
  path: '/api/v1/pan/verify',
  operationId: 'Post_Pan_Verify',
  summary: 'PAN Verification',
  priority: 1,
};

/**
 * Runs one PAN Verification step in a live session, mirroring the workflow
 * builder's phone journey. Renders the input screen for the highest-priority
 * enabled API (PAN Verification → standard form, PAN Lite → optional-name form,
 * PAN OCR → upload/scan a PAN image). On failure, automatically advances to the
 * next enabled API (visible priority fallback). Step success/failure is owned by
 * the parent (SessionMobilePanel), which shows the final success screen only
 * once every step of the session is complete.
 */
export function PanStepRunner({
  session,
  step,
}: {
  session: SessionDetailDto;
  step: SessionStepDto;
}) {
  const { mutate, isPending } = useVerifyStep(session.id);

  const bindings = useMemo(() => {
    const b = step.apiBindings.slice().sort((x, y) => x.priority - y.priority);
    return b.length > 0 ? b : [DEFAULT_BINDING];
  }, [step.apiBindings]);

  const [activeIdx, setActiveIdx] = useState(0);
  const [pan, setPan] = useState('');
  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
  const [fallbackNote, setFallbackNote] = useState<string | null>(null);
  // Set when a submit is blocked because the session's validity window elapsed.
  const [expired, setExpired] = useState(false);

  const cameraRef = useRef<HTMLInputElement>(null);

  const active = bindings[activeIdx] ?? bindings[0]!;
  const kind = panScreenKind(active);

  // PAN format gate (form APIs only). The Verify button stays disabled until the
  // PAN matches the format; the error text shows once the applicant has typed
  // something invalid (not on an empty field).
  const normalizedPan = pan.trim().toUpperCase();
  const panValid = PAN_REGEX.test(normalizedPan);
  const panError =
    normalizedPan.length > 0 && !panValid ? 'Enter a valid PAN (e.g. ABCDE1234F).' : undefined;

  // Session timed out (Settings → Session Expiry) → neutral "expired" screen.
  // `expired` is set when a Verify is rejected mid-flow; `session.isExpired` is
  // the backend's on-load flag so a resumed-but-expired session shows this
  // immediately instead of the form.
  if (expired || session.isExpired) {
    return <SessionExpiredScreen />;
  }

  // Business reject (step Failed) → terminal rejection screen. A reject ends the
  // session (it is now Rejected), so there is no retry.
  if (step.status === 'Failed') {
    return <FailureScreen reason={extractReason(step.resultJson)} />;
  }

  // Required fields per API. PAN OCR needs a captured/uploaded image; the form
  // APIs (PAN Verification + PAN Lite) both require PAN number and full name —
  // the Lite endpoint mandates the name too (docs.signcare.io pan-lite).
  function validate(k: PanScreenKind): boolean {
    if (k === 'ocr') return imageDataUrl !== null;
    // DOB is required only when Return-Data enables date_of_birth/age (step.requiresDob).
    const dobOk = !step.requiresDob || dob.trim().length > 0;
    return panValid && name.trim().length > 0 && dobOk;
  }

  function buildInput(k: PanScreenKind): Record<string, unknown> {
    if (k === 'ocr') {
      // KYC OCR request (PAN document) — shape matches the real SignCare
      // /api/v1/KycOCR contract (KycOcrRequest). fileBase64 is the raw base64
      // without the "data:image/...;base64," prefix. pdfPassword/returnQualityChecks/
      // responseType are required by the endpoint.
      const base64 = (imageDataUrl ?? '').replace(/^data:[^;]+;base64,/, '');
      return {
        fileBase64: base64,
        pdfPassword: 'N',
        requiredConfidence: true,
        requiredOcr: true,
        returnQualityChecks: ['ALL'],
        maskAadhaarImage: '',
        maskAadhaarText: '',
        responseFormat: 'DOCUMENT',
        responseType: 'URL',
        docTypes: ['PAN'],
      };
    }
    // Applicant data only — the backend verifier's NormalizeInput injects the
    // consent trio (consent / consent_text / request_id) and any renames. This
    // is the canonical split: frontend sends data, backend shapes the request.
    return {
      pan_number: pan.trim().toUpperCase(),
      pan_holder_name: name.trim(),
      ...(dob.trim() ? { dob: dob.trim() } : {}),
    };
  }

  function handleSubmit() {
    if (!validate(kind)) return;
    submitInput(buildInput(kind));
  }

  // DEV: one-click test PAN — submits a fixed test PAN (backend PanMockResponses
  // returns the canned result) through the SAME flow so rules/fallback run.
  function runTestPan(panNumber: string) {
    submitInput({
      pan_number: panNumber,
      pan_holder_name: 'Test User',
    });
  }

  function submitInput(input: Record<string, unknown>) {
    mutate(
      {
        stepId: step.id,
        input,
        catalogueEntryId: active.catalogueEntryId || undefined,
      },
      {
        onSuccess: (data) => {
          const updated = data.steps.find((s) => s.id === step.id);
          // Provisional shared-link mode: the returned session has fresh step ids
          // (a new/resumed session), so there's no match here — the entry screen
          // navigates to the real run on success. Nothing to do in this handler.
          if (!updated) return;
          const status = updated?.status;
          if (status === 'Passed' || status === 'Review') {
            // Step done — parent advances to the next step (or the final
            // success screen once all steps are complete).
            return;
          }
          if (status === 'Failed') {
            // Business rejection (invalid/inactive PAN, or a Reject risk-rule /
            // condition). This is a FINAL verdict, NOT a technical failure — do
            // NOT fall back to the next API (OCR can't make an invalid PAN valid).
            // The parent re-renders the failure screen from the updated step.
            return;
          }
          // status Pending → the API itself failed technically (provider down /
          // error). THIS is when we fall back to the next enabled API.
          const nextBinding = bindings[activeIdx + 1];
          if (nextBinding) {
            // Applicant-facing: never the provider's internal name (`summary`).
            // Same wording as the other runners.
            setFallbackNote(tryAnotherMethodNote(PAN_METHOD_LABEL[panScreenKind(nextBinding)]));
            setActiveIdx(activeIdx + 1);
            setImageDataUrl(null);
          } else {
            toast.error(CHECK_FAILED_TOAST);
            // All APIs exhausted with a technical error — the step stays
            // Pending; surface a retry from binding 1.
            setFallbackNote(RETRY_EXHAUSTED_NOTE);
            setActiveIdx(0);
            setImageDataUrl(null);
          }
        },
        onError: (error) => {
          if (isSessionExpiredError(error)) {
            setExpired(true);
            return;
          }
          toast.error('Could not reach the server. Please try again.');
        },
      },
    );
  }

  function onFileSelected(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setImageDataUrl(reader.result as string);
    reader.readAsDataURL(file);
    e.target.value = ''; // allow re-selecting the same file
  }

  const formValues = { panNumber: pan, fullName: name };
  const applyFormPatch = (patch: { panNumber?: string; fullName?: string }) => {
    if (patch.panNumber !== undefined) setPan(patch.panNumber);
    if (patch.fullName !== undefined) setName(patch.fullName);
  };

  // Live PAN card that fills in as the applicant types (designer's larger mock).
  const liveCard = (
    <PanCardMock
      panNumber={pan || undefined}
      name={name || undefined}
      dob={dob.trim() || undefined}
    />
  );

  return (
    // `flex-1` so the runner fills the card: the action buttons below carry `mt-auto`
    // and therefore sit at the BOTTOM on every step, in the same place, with the
    // "Secured by" line beneath them. Without it each screen put its button wherever
    // its content happened to end, so the button jumped as the applicant advanced.
    <div className="flex flex-1 flex-col gap-4">
      {/* DEV-ONLY: one-click test PANs (backend PanMockResponses) to exercise the
          step's Risk / Advanced Rules. Stripped from production builds. */}
      {import.meta.env.DEV && <PanDevSimulator onRun={runTestPan} isPending={isPending} />}

      {/* Priority-fallback banner (only appears when a technical failure falls
          back to the next API — not shown otherwise). */}
      {fallbackNote && (
        <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2 text-xs text-[#92400E]">
          {fallbackNote}
        </div>
      )}

      {/* Designer input screen for the active API.
          WRAPPED, and that wrapper is load-bearing. These screens return a FRAGMENT — a
          header, the card, three fields, a privacy note — so without it every one of those
          rows became a direct child of the `gap-4` flex above and picked up a 16px gap ON
          TOP of the margins the designer already gave them. That stacked roughly 60px of
          space the design never asked for (28px between the title and the PAN card alone)
          and was what pushed this screen — the tallest in the set — past the viewport into
          a scroll. Inside a plain block the designer's own margins govern, so the screen
          now measures what it measures in their mock. */}
      <div>
        {kind === 'standard' && (
          <PanVerificationStandard
            dob={dob}
            setDob={setDob}
            values={formValues}
            onChange={applyFormPatch}
            card={liveCard}
            panError={panError}
            dobRequired={step.requiresDob}
          />
        )}
        {kind === 'lite' && (
          <PanVerificationLite
            dob={dob}
            setDob={setDob}
            values={formValues}
            onChange={applyFormPatch}
            card={liveCard}
            panError={panError}
            dobRequired={step.requiresDob}
          />
        )}
        {kind === 'ocr' && (
          <>
            <PanVerificationOCR>
              {imageDataUrl ? (
                // Fixed-size preview — a large upload is cropped to fit the frame
                // instead of stretching the screen into a long scroll.
                <img
                  src={imageDataUrl}
                  alt="Captured PAN"
                  className="h-[190px] w-full rounded-xl object-cover"
                />
              ) : undefined}
            </PanVerificationOCR>

            {/* Camera capture only — Capture / Recapture. No file upload (the applicant
                photographs the PAN card live on their phone).
                `mt-3` is explicit now that this sits inside the wrapper above rather than
                in the gap-4 column, which used to space it for free. */}
            <button
              type="button"
              onClick={() => cameraRef.current?.click()}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-primary py-2.5 text-sm font-medium text-primary hover:bg-primary/5"
            >
              <Camera size={16} />
              {imageDataUrl ? 'Recapture PAN' : 'Capture PAN'}
            </button>
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={onFileSelected}
            />
          </>
        )}
      </div>

      {/* Verify is enabled only once all required fields for the active API
          are filled (PAN + Full Name for the forms, an image for OCR). */}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={isPending || !validate(kind)}
        style={{
          backgroundColor: 'var(--brand-btn-fill, var(--color-primary))',
          color: 'var(--brand-btn-text, #ffffff)',
          borderColor: 'var(--brand-btn-stroke, var(--color-primary))',
          borderRadius: 'var(--brand-btn-radius, 0.75rem)',
        }}
        className="mt-auto flex w-full items-center justify-center gap-2 border py-3 text-[15px] font-semibold transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
      >
        {isPending && <Loader2 size={15} className="animate-spin" />}
        Continue
      </button>
    </div>
  );
}

// Shown when a PAN step is rejected (business Failed). The verification returned a
// definitive negative result (e.g. invalid/inactive PAN, or a Reject risk-rule),
// which ends the session (now Rejected). This is terminal — no retry.
function FailureScreen({ reason }: { reason?: string | null }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-4 text-center">
      <div className="mb-6 flex h-[110px] w-[110px] items-center justify-center rounded-full bg-[#DC2626]">
        <X size={60} strokeWidth={3} color="white" />
      </div>
      <h2 className="mb-2 text-[length:var(--text-20)] font-semibold leading-tight text-[#DC2626]">
        Verification failed
      </h2>
      <p className="text-[length:var(--text-14)] text-[#666666]">
        {reason ?? 'The details entered could not be verified. This session has been rejected.'}
      </p>
    </div>
  );
}
